<template>
	<Col class="width-100 height-100 flex-centerize">
		<PublishButton
			v-if="initialized"
			v-show="false"
			canPublish
			showCloseBuilderButton
			@close-builder="closeBuilder"
			ref="publish_button"
			:needPublish="isPublishNeeded"
			:problematicNodes="problematicNodes"
			:allowedToPublish="allowedToPublish"
			@publish-started="didPublishStart = true"
			@publish-completed="isPublishCompleted = true"
		/>
		<Col class="width-100 height-100" v-if="didPublishStart">
			<ShareFeatureDialogHeader :workflow="workflow" :service="service" />
			<PublishingAnimation class="mt-n12" :workflowId="workflowId" :isPublishCompleted="isPublishCompleted" />
		</Col>
		<Row v-else-if="showErrorMessage">Something went wrong. Please try again later</Row>
		<AppLoader class="width-100 height-100 flex-centerize" v-else />
	</Col>
</template>
<script>
import AppService from '@/utils/AppService';
import { getService } from '@/store/modules/apps/getters';
import { getAction } from '@/store/modules/actions/getters';
import { ProjectPermissions } from '@ply-io/ply-front-shared-utilities';
import * as WorkflowNodeService from '@/utils/workflow-node-service';
import { getWorkflow } from '@/store/modules/workflows/getters';
import { getServiceBySlug } from '@/store/modules/apps/getters';
import PublishButton from '@/components/Project/PublishButton.vue';
import { projectHasPermission } from '@/store/modules/projects/getters';
import { nodesDataArray } from '@/store/modules/selectedWorkflow/getters';
import { selectWorkflowId } from '@/store/modules/selectedWorkflow/actions';
import PublishingAnimation from '@/components/Project/PublishingAnimation.vue';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';
import ExtensionCommunicationService from '@/utils/extension-communication-service';
import { isStatusPublishValid } from '@/components/BlockEditor/block-editor-service.js';
import ShareFeatureDialogHeader from '@/components/dialogs/ShareFeatureDialog/ShareFeatureDialogHeader.vue';
import IframeService from '@/utils/iframe-service';
export default {
	name: 'ExtensionPublishWorkflow',
	props: { workflowId: { type: [String, Number] } },
	components: { PublishingAnimation, PublishButton, ShareFeatureDialogHeader },
	computed: {
		...mapGettersShorthand({ projectHasPermission, getWorkflow, getService, nodesDataArray, getAction, getServiceBySlug }),
		isPublished() {
			return this.workflow?.is_published;
		},
		isPublishNeeded() {
			return this.workflow && (this.workflow.draft_pending_changes_flag || !this.workflow.is_published);
		},
		workflow() {
			return this.getWorkflow(Number(this.workflowId));
		},
		problematicNodes() {
			return this.relevantNodes.filter((workflowNode) => !this.isNodeStatusPublishValid(workflowNode));
		},
		relevantNodes() {
			return this.nodesDataArray.filter((node) => !WorkflowNodeService.isNodeOrphan(node));
		},
		allowedToPublish() {
			return this.projectHasPermission(this.workflow.project, ProjectPermissions.PublishWorkflows);
		},
		initialized() {
			return !this.isLoading && !this.showErrorMessage;
		},
		service() {
			return this.contextServiceSlug ? this.getServiceBySlug(this.contextServiceSlug) : null;
		},
		contextServiceSlug() {
			return this.workflow.context_service_slug;
		},
	},
	methods: {
		...mapActionsShorthand({ selectWorkflowId }),
		async setWorkflowAsSelected() {
			this.isLoading = true;
			try {
				await this.selectWorkflowId({ id: Number(this.workflowId) });
			} catch {
				this.showErrorMessage = true;
			} finally {
				this.isLoading = false;
			}
		},
		isNodeStatusPublishValid(workflowNode) {
			const { service: serviceID } = workflowNode;
			const service = this.getService(serviceID);
			if (service && AppService.isSlugOfWebhook(service.service_slug)) {
				// switch to handle special services like ply webhooks (default successful test result)
				return true;
			}
			if (WorkflowNodeService.isConsolidateDataNode(workflowNode, this.getAction)) {
				const branchVariables = workflowNode?.branch_variables || [];
				const areThereMappingsFromBranchVariables = branchVariables.some((branchVariable) => branchVariable.mappings.length > 0);
				if (!areThereMappingsFromBranchVariables) {
					return true;
				}
			}

			return isStatusPublishValid(workflowNode.test_result_status);
		},
		closeBuilder() {
			IframeService.sendMessageToParent('flow_saved');
		},
	},
	async created() {
		await this.setWorkflowAsSelected();
		ExtensionCommunicationService.notifyExtensionPageCreated();
	},
	data() {
		return {
			isLoading: true,
			didPublishStart: false,
			showErrorMessage: false,
			isPublishCompleted: false,
		};
	},
	watch: {
		initialized(newV) {
			if (newV) {
				this.$nextTick(async () => {
					await this.$refs.publish_button.startPublish();
				});
			}
		},
	},
};
</script>
